import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

/**
 * 格式化出生时间为 HH:mm:ss 格式
 */
const formatBirthTime = (time: string): string | undefined => {
  if (!time || !time.trim()) return undefined;
  // 如果只有 HH:mm，添加 :00
  if (time.length === 5 && time.includes(':')) {
    return time + ':00';
  }
  return time;
};

/**
 * 注册函数
 * @param {Object} userData 用户注册信息
 * @param {string} userData.username 用户名
 * @param {string} userData.password 密码
 * @param {string} userData.email 邮箱
 * @param {string} userData.emailCode 邮箱验证码
 * @param {string} userData.birthday 生日（必填，格式：YYYY-MM-DD）
 * @param {string} userData.birthplace 出生地（可选）
 * @param {string} userData.birthtime 出生时间（可选，格式：HH:mm 或 HH:mm:ss）
 * @returns {Promise<Object>} 返回后端的响应数据
 */
export const signUp = async (userData: {
  username: string;
  password: string;
  email: string;
  emailCode: string;
  birthday?: string;
  birthplace?: string;
  birthtime?: string;
}): Promise<any> => {
  try {
    const apiBaseUrl = getApiBaseUrl();

    // 构建请求数据，确保格式正确
    const requestData: any = {
      username: userData.username.trim(),
      password: userData.password,
      email: userData.email.trim(),
      emailCode: userData.emailCode.trim(),
    };

    // birthday 是必填字段（数据库要求 NOT NULL）
    if (userData.birthday && userData.birthday.trim()) {
      requestData.birthday = userData.birthday.trim();
    } else {
      throw new Error('Birthday is required');
    }

    // 可选字段：如果为空，发送 null（而不是空字符串）
    // 这样后端会接收到 null，传递给 Hasura 时可以正确处理（因为字段是可选的）
    if (userData.birthplace && userData.birthplace.trim()) {
      requestData.birthplace = userData.birthplace.trim();
    } else {
      // 发送 null 而不是空字符串，Hasura 可以正确处理 null
      requestData.birthplace = null;
    }

    // 格式化 birthtime：如果为空，发送 null
    if (userData.birthtime && userData.birthtime.trim()) {
      const formattedTime = formatBirthTime(userData.birthtime.trim());
      if (formattedTime) {
        requestData.birthtime = formattedTime;
      } else {
        // 如果格式化失败，发送 null
        requestData.birthtime = null;
      }
    } else {
      // 如果为空，发送 null（Hasura 可以正确处理 null，但不能处理空字符串）
      requestData.birthtime = null;
    }

    // console.log('注册请求 URL:', `${apiBaseUrl}/signup`);
    // console.log('注册数据:', { ...requestData, password: '***' }); // 不打印密码

    const response = await axios.post(
      `${apiBaseUrl}/signup`,
      requestData,
      {
        headers: {
        },
      }
    );

    return response.data; // 返回后端响应数据
  } catch (err: any) {
    console.error('注册失败 - 完整错误信息:', err);
    console.error('注册失败 - 响应数据:', err.response?.data);
    console.error('注册失败 - 响应状态:', err.response?.status);

    // 处理 HTTP 错误响应
    if (err.response) {
      const errorData = err.response.data;
      console.error('注册失败 - 错误数据:', errorData);

      if (errorData?.error) {
        // 根据后端返回的错误信息，提供更友好的提示
        if (errorData.error === 'User already exists') {
          throw new Error('用户已存在，请使用其他用户名或邮箱');
        } else if (errorData.error === 'Failed to create user') {
          // 如果有详细信息，显示详细信息
          const details = errorData.details;
          if (details) {
            console.error('注册失败详情:', details);
            // 尝试格式化错误信息
            let errorMsg = '注册失败：';
            if (Array.isArray(details)) {
              errorMsg += details.map((d: any) => d.message || JSON.stringify(d)).join(', ');
            } else if (typeof details === 'object') {
              errorMsg += JSON.stringify(details);
            } else {
              errorMsg += details;
            }
            throw new Error(errorMsg);
          }
          throw new Error('注册失败，可能是数据库连接问题，请稍后重试');
        } else {
          throw new Error(errorData.error);
        }
      } else {
        // 如果没有 error 字段，尝试显示整个响应数据
        const errorMsg = errorData?.message || JSON.stringify(errorData) || '未知错误';
        throw new Error(`注册失败: ${errorMsg}`);
      }
    }

    // 处理网络错误或其他错误
    if (err.message) {
      throw new Error(err.message);
    }

    throw new Error('注册失败，请检查网络连接或稍后重试');
  }
};

